import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    TextField,
    Stack,
    Tabs,
    Tab,
    Paper
} from '@mui/material';
import {
    Close as CloseIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    DragIndicator as DragIcon,
    Message as MessageIcon,
    Image as ImageIcon,
    Mic as AudioIcon,
    Videocam as VideoIcon,
    Timer as TimerIcon,
    InsertDriveFile as DocumentIcon,
    Visibility as EyeIcon,
    FolderOpen as FolderOpenIcon,
    Folder as FolderIcon,
    Home as HomeIcon,
    NavigateNext as NavigateNextIcon,
    ArrowBack as ArrowBackIcon,
    Search as SearchIcon,
    Image as ImgIcon,
    VideoFile as VidFileIcon,
    AudioFile as AudFileIcon,
    Description as DocFileIcon,
    InsertDriveFile as GenericFileIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { api } from '../../../services/api';
import SysEditorWhatsapp from '../../../components/syseditor/SysEditorWhatsapp';
import { FLOW_BOT_SINGLE_BLOCK_EXTRA_VARIABLES, FLOW_BOT_VARIABLES } from './flowBotVariables';
import { env } from '../../../utils/env';
import {
    ListItem,
    ListItemButton,
    ListItemIcon,
    InputAdornment,
    Breadcrumbs,
    Link,
    Tooltip as MuiTooltip,
    CircularProgress,
    List
} from '@mui/material';

const FlowBuilderSingleBlockModal = ({ open, onClose, onSave, data }: any) => {
    const [elements, setElements] = useState<any[]>([]);
    const [activeTab, setActiveTab] = useState(0);
    const [newMessage, setNewMessage] = useState('');
    const [newInterval, setNewInterval] = useState(1);
    const [uploading, setUploading] = useState(false);
    const [previewData, setPreviewData] = useState<{ url: string; type: string } | null>(null);

    const baseUrl = env.API_URL;

    // States for Local File Picker
    const [localFileDialogOpen, setLocalFileDialogOpen] = useState(false);
    const [localFiles, setLocalFiles] = useState<any[]>([]);
    const [localPath, setLocalPath] = useState('');
    const [localSearch, setLocalSearch] = useState('');
    const [localLoading, setLocalLoading] = useState(false);
    const [selectedLocalFile, setSelectedLocalFile] = useState<any | null>(null);
    const [addingLocalFile, setAddingLocalFile] = useState(false);

    useEffect(() => {
        if (open && data) {
            // Reconstruir o estado dos elementos a partir dos dados do nó
            const loadedElements: any[] = [];
            const seq = data.seq || [];
            const savedElements = data.elements || [];

            seq.forEach((id: string) => {
                const element = savedElements.find((e: any) => e.number === id);
                if (element) {
                    loadedElements.push({
                        id: element.number,
                        type: element.type,
                        value: element.value,
                        // Para mídias, tentar recuperar o nome original se possível, senão usar o valor (url)
                        originalName: element.options?.originalName || element.value
                    });
                }
            });
            setElements(loadedElements);
        } else {
            setElements([]);
            setActiveTab(0);
            setNewMessage('');
            setNewInterval(1);
            setUploading(false);
            setDraggedItemIndex(null);
            setDropTargetIndex(null);
            setPreviewData(null);
        }
    }, [open, data]);

    const handleAddMessage = () => {
        if (!newMessage.trim()) return;

        const newElement = {
            id: `msg_${Date.now()}`,
            type: 'message',
            value: newMessage
        };

        setElements([...elements, newElement]);
        setNewMessage('');
    };

    const handleAddInterval = () => {
        const interval = parseInt(newInterval as any);
        if (isNaN(interval) || interval < 1) return;

        const newElement = {
            id: `interval_${Date.now()}`,
            type: 'interval',
            value: interval.toString()
        };

        setElements([...elements, newElement]);
        setNewInterval(1);
    };

    const handleFileUpload = async (event: any, type: 'image' | 'audio' | 'video' | 'document') => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('medias', file);

        setUploading(true);
        try {
            const data = await api.post('/atendimento/fluxos/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }) as unknown as any[];

            // A resposta é um array de objetos { originalName, filename, type, url }
            const uploadedFile = data[0];

            const newElement = {
                id: `${type}_${Date.now()}`,
                type: type,
                value: uploadedFile.url,
                originalName: uploadedFile.originalName
            };

            setElements([...elements, newElement]);
            toast.success('Arquivo enviado com sucesso!');
        } catch (err) {
            toast.error('Erro ao enviar arquivo.');
            console.error(err);
        } finally {
            setUploading(false);
            // Limpar o input
            event.target.value = '';
        }
    };

    const loadLocalFiles = async (subpath: string) => {
        setLocalLoading(true);
        try {
            const params = subpath ? { subpath } : {};
            const data = await api.get('/atendimento/fluxos/files', { params }) as any;
            setLocalFiles(data.files || []);
        } catch {
            setLocalFiles([]);
        } finally {
            setLocalLoading(false);
        }
    };

    const handleSelectLocalFile = async () => {
        if (!selectedLocalFile) return;

        setAddingLocalFile(true);
        try {
            const typeMap: Record<string, string> = {
                'image': 'image',
                'video': 'video',
                'audio': 'audio',
                'document': 'document'
            };

            const type = typeMap[selectedLocalFile.type] || 'document';

            const newElement = {
                id: `${type}_${Date.now()}`,
                type: type,
                value: selectedLocalFile.path,
                originalName: selectedLocalFile.name
            };

            setElements([...elements, newElement]);
            setLocalFileDialogOpen(false);
            setSelectedLocalFile(null);
            toast.success('Arquivo local adicionado!');
        } catch (error) {
            console.error('Erro ao adicionar arquivo local:', error);
            toast.error('Erro ao adicionar arquivo local');
        } finally {
            setAddingLocalFile(false);
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const getLocalFileIcon = (type: string) => {
        switch (type) {
            case 'folder': return <FolderIcon sx={{ color: '#FFA726' }} />;
            case 'image': return <ImgIcon sx={{ color: '#42A5F5' }} />;
            case 'video': return <VidFileIcon sx={{ color: '#AB47BC' }} />;
            case 'audio': return <AudFileIcon sx={{ color: '#26A69A' }} />;
            case 'document': return <DocFileIcon sx={{ color: '#EF5350' }} />;
            default: return <GenericFileIcon sx={{ color: '#78909C' }} />;
        }
    };

    const handleRemoveElement = (id: string) => {
        setElements(elements.filter(e => e.id !== id));
    };

    const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
    const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
        setDraggedItemIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (draggedItemIndex === index) return;
        setDropTargetIndex(index);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>, index: number) => {
        e.preventDefault();
        setDropTargetIndex(null);
        if (draggedItemIndex === null || draggedItemIndex === index) return;

        const newElements = [...elements];
        const [draggedItem] = newElements.splice(draggedItemIndex, 1);
        newElements.splice(index, 0, draggedItem);

        setElements(newElements);
        setDraggedItemIndex(null);
    };



    const handleSave = () => {
        // Preparar dados para salvar no formato esperado pelo backend/nó
        const seq = elements.map(e => e.id);
        const savedElements = elements.map(e => ({
            number: e.id,
            type: e.type,
            value: e.value,
            options: {
                originalName: e.originalName
            }
        }));

        onSave({
            seq,
            elements: savedElements
        });
        onClose();
    };

    const renderElementIcon = (type: string) => {
        switch (type) {
            case 'message': return <MessageIcon color="primary" />;
            case 'interval': return <TimerIcon color="warning" />;
            case 'image': return <ImageIcon color="success" />;
            case 'audio': return <AudioIcon color="error" />;
            case 'video': return <VideoIcon color="secondary" />;
            case 'document': return <DocumentIcon color="primary" />;
            default: return <MessageIcon />;
        }
    };

    const renderElementContent = (element: any) => {
        if (element.type === 'message') {
            return (
                <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                    {element.value}
                </Typography>
            );
        }
        if (element.type === 'interval') {
            return (
                <Typography variant="body2">
                    Aguardar <strong>{element.value}</strong> segundos
                </Typography>
            );
        }
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" noWrap sx={{ maxWidth: element.type === 'audio' ? 200 : 250 }}>
                    {element.originalName || element.value}
                </Typography>
                {element.type === 'image' && (
                    // eslint-disable-next-line jsx-a11y/alt-text
                    <img src={`${baseUrl}/uploads/${element.value}`} style={{ height: 50, width: 'auto', objectFit: 'cover', borderRadius: 4 }} />
                )}
                {element.type === 'document' && <DocumentIcon sx={{ height: 30, width: 30 }} color="primary" />}
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Configurar Bloco de Conteúdo
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', height: 600 }}>
                    {/* Painel Esquerdo: Adicionar Itens */}
                    <Box sx={{ width: '40%', borderRight: '1px solid #e0e0e0', pr: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Adicionar Elemento
                        </Typography>

                        <Tabs
                            value={activeTab}
                            onChange={(_, v) => setActiveTab(v)}
                            variant="fullWidth"
                            sx={{ mb: 2 }}
                        >
                            <Tab icon={<MessageIcon />} aria-label="Texto" />
                            <Tab icon={<ImageIcon />} aria-label="Mídia" />
                            <Tab icon={<TimerIcon />} aria-label="Intervalo" />
                        </Tabs>

                        {activeTab === 0 && (
                            <Stack spacing={1}>
                                <SysEditorWhatsapp
                                    value={newMessage}
                                    onChange={setNewMessage}
                                    placeholder="Digite sua mensagem aqui... Use {{nome_contato}} para variáveis."
                                    rows={6}
                                    showVariables
                                    variables={[...FLOW_BOT_VARIABLES, ...FLOW_BOT_SINGLE_BLOCK_EXTRA_VARIABLES]}
                                />
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddMessage}
                                    disabled={!newMessage.trim()}
                                >
                                    Adicionar Texto
                                </Button>
                            </Stack>
                        )}

                        {activeTab === 1 && (
                            <Stack spacing={2}>
                                <Typography variant="caption" color="textSecondary">
                                    Suporta Imagens, Áudio, Vídeo e Documentos.
                                </Typography>

                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<ImageIcon />}
                                    fullWidth
                                    color="success"
                                >
                                    Upload Imagem
                                    <input type="file" hidden accept="image/*" onChange={(e) => handleFileUpload(e, 'image')} />
                                </Button>

                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<AudioIcon />}
                                    fullWidth
                                    color="error"
                                >
                                    Upload Áudio
                                    <input type="file" hidden accept="audio/*" onChange={(e) => handleFileUpload(e, 'audio')} />
                                </Button>

                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<VideoIcon />}
                                    fullWidth
                                    color="secondary"
                                >
                                    Upload Vídeo
                                    <input type="file" hidden accept="video/*" onChange={(e) => handleFileUpload(e, 'video')} />
                                </Button>

                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<DocumentIcon />}
                                    fullWidth
                                    color="info"
                                >
                                    Upload Documento
                                    <input type="file" hidden accept="*/*" onChange={(e) => handleFileUpload(e, 'document')} />
                                </Button>

                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setLocalFileDialogOpen(true);
                                        setLocalPath('');
                                        setLocalSearch('');
                                        setSelectedLocalFile(null);
                                        loadLocalFiles('');
                                    }}
                                    fullWidth
                                    startIcon={<FolderOpenIcon />}
                                    sx={{ color: '#FF7043', borderColor: '#FF7043', '&:hover': { borderColor: '#E64A19', bgcolor: 'rgba(255, 112, 67, 0.04)' } }}
                                >
                                    Arquivo Local
                                </Button>

                                {uploading && <Typography variant="caption">Enviando arquivo...</Typography>}
                            </Stack>
                        )}

                        {activeTab === 2 && (
                            <Stack spacing={2}>
                                <TextField
                                    label="Intervalo (segundos)"
                                    type="number"
                                    fullWidth
                                    value={newInterval}
                                    onChange={(e) => setNewInterval(Number(e.target.value))}
                                    InputProps={{ inputProps: { min: 1 } }}
                                />
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddInterval}
                                    color="warning"
                                >
                                    Adicionar Intervalo
                                </Button>
                            </Stack>
                        )}
                    </Box>

                    {/* Painel Direito: Lista de Itens */}
                    <Box sx={{ width: '60%', pl: 1, pr: 1, overflowY: 'auto' }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Sequência de Execução ({elements.length} itens)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Para mudar ordem basta arrastar e soltar
                        </Typography>

                        {elements.length === 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.5 }}>
                                <DragIcon sx={{ fontSize: 48, mb: 1 }} />
                                <Typography>Nenhum elemento adicionado</Typography>
                            </Box>
                        ) : (
                            <Stack spacing={1}>
                                {elements.map((element, index) => (
                                    <Paper
                                        key={element.id}
                                        variant="outlined"
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, index)}
                                        onDragOver={(e) => handleDragOver(e, index)}
                                        onDrop={(e) => handleDrop(e, index)}
                                        sx={{
                                            p: 0.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.8,
                                            mr: 1,
                                            cursor: 'grab',
                                            ' :active': { cursor: 'grabbing' },
                                            bgcolor: draggedItemIndex === index ? 'action.hover' : 'background.paper',
                                            border: '1px solid #e0e0e0',
                                            borderTop: dropTargetIndex === index && draggedItemIndex !== index ? '3px solid #1976d2' : '1px solid #e0e0e0',
                                            opacity: draggedItemIndex === index ? 0.5 : 1,
                                            transition: 'border-width 0.1s'
                                        }}
                                    >
                                        <Box sx={{ color: 'text.secondary' }}>
                                            {renderElementIcon(element.type)}
                                        </Box>

                                        <Box sx={{ flexGrow: 1 }}>
                                            {renderElementContent(element)}
                                        </Box>

                                        <Box sx={{ display: 'flex' }}>
                                            {element.type === 'audio' ? (
                                                <Box
                                                    sx={{
                                                        '& audio::-webkit-media-controls-volume-slider': { display: 'none !important' },
                                                        '& audio::-webkit-media-controls-mute-button': { display: 'none !important' },
                                                        '& audio::-webkit-media-controls-volume-control-container': { display: 'none !important' }
                                                    }}
                                                >
                                                    <audio
                                                        controls
                                                        src={`${baseUrl}/uploads/${element.value}`}
                                                        style={{ height: 32, width: 200 }}
                                                    />
                                                </Box>
                                            ) : (
                                                ['image', 'video', 'document'].includes(element.type) && (
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => {
                                                            const url = `${baseUrl}/uploads/${element.value}`;
                                                            if (element.type === 'document') {
                                                                window.open(url, '_blank');
                                                            } else {
                                                                setPreviewData({ url, type: element.type });
                                                            }
                                                        }}
                                                    >
                                                        <EyeIcon />
                                                    </IconButton>
                                                )
                                            )}

                                            <IconButton
                                                color="error"
                                                onClick={() => handleRemoveElement(element.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </Paper>
                                ))}
                            </Stack>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={handleSave} variant="contained" color="primary">
                    Salvar Bloco
                </Button>
            </DialogActions>

            <Dialog
                open={!!previewData}
                onClose={() => setPreviewData(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Visualizar Mídia
                    <IconButton
                        aria-label="close"
                        onClick={() => setPreviewData(null)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {previewData && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
                            {previewData.type === 'image' ? (
                                <img
                                    src={previewData.url}
                                    alt="Preview"
                                    style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                                />
                            ) : (
                                <video
                                    src={previewData.url}
                                    controls
                                    style={{ maxWidth: '100%', maxHeight: '70vh' }}
                                />
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewData(null)}>Fechar</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de Arquivo Local */}
            <Dialog
                open={localFileDialogOpen}
                onClose={() => !addingLocalFile && setLocalFileDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Selecionar Arquivo Local
                    <IconButton
                        onClick={() => !addingLocalFile && setLocalFileDialogOpen(false)}
                        size="small"
                        disabled={addingLocalFile}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {/* Breadcrumbs */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {localPath && (
                                <IconButton size="small" onClick={() => {
                                    const parts = localPath.split('/').filter(Boolean);
                                    parts.pop();
                                    const newPath = parts.join('/');
                                    setLocalPath(newPath);
                                    setSelectedLocalFile(null);
                                    loadLocalFiles(newPath);
                                }}>
                                    <ArrowBackIcon fontSize="small" />
                                </IconButton>
                            )}
                            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ flex: 1 }}>
                                <Link
                                    component="button"
                                    underline="hover"
                                    onClick={() => {
                                        setLocalPath('');
                                        setSelectedLocalFile(null);
                                        loadLocalFiles('');
                                    }}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', fontSize: '0.85rem' }}
                                >
                                    <HomeIcon fontSize="small" /> Raiz
                                </Link>
                                {localPath.split('/').filter(Boolean).map((part, idx, arr) => {
                                    const pathUpTo = arr.slice(0, idx + 1).join('/');
                                    return idx === arr.length - 1 ? (
                                        <Typography key={pathUpTo} fontWeight={600} fontSize="0.85rem">{part}</Typography>
                                    ) : (
                                        <Link
                                            key={pathUpTo}
                                            component="button"
                                            underline="hover"
                                            onClick={() => {
                                                setLocalPath(pathUpTo);
                                                setSelectedLocalFile(null);
                                                loadLocalFiles(pathUpTo);
                                            }}
                                            sx={{ cursor: 'pointer', fontSize: '0.85rem' }}
                                        >
                                            {part}
                                        </Link>
                                    );
                                })}
                            </Breadcrumbs>
                        </Box>

                        {/* Search */}
                        <TextField
                            fullWidth
                            placeholder="Buscar arquivos..."
                            value={localSearch}
                            onChange={(e) => setLocalSearch(e.target.value)}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* File list */}
                        {localLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <List sx={{ maxHeight: 350, overflow: 'auto', p: 0 }}>
                                {localFiles
                                    .filter(f => !localSearch || f.name.toLowerCase().includes(localSearch.toLowerCase()))
                                    .map((file) => (
                                        <ListItem key={file.path} disablePadding>
                                            <ListItemButton
                                                onClick={() => {
                                                    if (file.isDirectory) {
                                                        setLocalPath(file.path);
                                                        setSelectedLocalFile(null);
                                                        loadLocalFiles(file.path);
                                                    } else {
                                                        setSelectedLocalFile(file);
                                                    }
                                                }}
                                                selected={selectedLocalFile?.path === file.path}
                                                disabled={addingLocalFile}
                                                sx={{ py: file.type === 'audio' ? 1 : 0.5 }}
                                            >
                                                <ListItemIcon sx={{ minWidth: file.type === 'image' ? 52 : 36 }}>
                                                    {file.type === 'video' && (
                                                        <Box
                                                            component="video"
                                                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                            sx={{ maxWidth: 150, maxHeight: 150, mt: 0.5, borderRadius: 1, mr: 1, bgcolor: 'black' }}
                                                        >
                                                            <source src={`${baseUrl}/uploads/${file.path}`} />
                                                        </Box>
                                                    )}
                                                    {file.type === 'image' ? (
                                                        <Box
                                                            component="img"
                                                            src={`${baseUrl}/uploads/${file.path}`}
                                                            alt={file.name}
                                                            sx={{
                                                                width: 60,
                                                                height: 60,
                                                                mr: 1,
                                                                borderRadius: 1,
                                                                objectFit: 'cover',
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                            }}
                                                        />
                                                    ) : file.type !== 'video' && getLocalFileIcon(file.type)}


                                                </ListItemIcon>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography fontSize="0.9rem" fontWeight={file.isDirectory ? 600 : 400} noWrap>
                                                        {file.name}
                                                    </Typography>
                                                    <Typography fontSize="0.75rem" color="text.secondary">
                                                        {file.isDirectory ? 'Pasta' : formatFileSize(file.size)}
                                                    </Typography>
                                                    {file.type === 'audio' && (
                                                        <Box
                                                            component="audio"
                                                            controls
                                                            preload="none"
                                                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                            sx={{ width: '100%', height: 32, mt: 0.5 }}
                                                        >
                                                            <source src={`${baseUrl}/uploads/${file.path}`} />
                                                        </Box>
                                                    )}
                                                </Box>
                                                {file.name.toLowerCase().endsWith('.pdf') && (
                                                    <MuiTooltip title="Visualizar PDF">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const fileUrl = `${baseUrl}/uploads/${file.path}`;
                                                                window.open(fileUrl, '_blank');
                                                            }}
                                                        >
                                                            <EyeIcon fontSize="small" />
                                                        </IconButton>
                                                    </MuiTooltip>
                                                )}
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                {localFiles.filter(f => !localSearch || f.name.toLowerCase().includes(localSearch.toLowerCase())).length === 0 && (
                                    <Box sx={{ textAlign: 'center', p: 3 }}>
                                        <Typography color="text.secondary" variant="body2">
                                            {localSearch ? 'Nenhum arquivo encontrado' : 'Pasta vazia'}
                                        </Typography>
                                    </Box>
                                )}
                            </List>
                        )}

                        {/* Selected file info */}
                        {selectedLocalFile && (
                            <Paper sx={{ p: 1.5, bgcolor: 'primary.light', color: 'primary.contrastText', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                {getLocalFileIcon(selectedLocalFile.type)}
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={600}>{selectedLocalFile.name}</Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.9 }}>{formatFileSize(selectedLocalFile.size)}</Typography>
                                </Box>
                            </Paper>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setLocalFileDialogOpen(false)}
                        disabled={addingLocalFile}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSelectLocalFile}
                        disabled={addingLocalFile || !selectedLocalFile}
                        startIcon={addingLocalFile ? <CircularProgress size={20} /> : undefined}
                    >
                        {addingLocalFile ? 'Adicionando...' : 'Adicionar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog >
    );
};

export default FlowBuilderSingleBlockModal;
